<template>
  <div class="deck-builder">
    <!-- Header with “+” button -->
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h2>Deck Builder</h2>
      <button class="btn btn-primary" @click="openNewDeckDialog" title="New Deck">
        <i class="bi bi-plus-lg"></i>
      </button>
    </div>

    <!-- New Deck Modal -->
    <div class="modal fade show" tabindex="-1" v-if="showNewDeckDialog" style="display: block;">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Create New Deck</h5>
            <button type="button" class="btn-close" @click="closeNewDeckDialog"></button>
          </div>
          <div class="modal-body">
            <input v-model="modalDeckName" type="text" class="form-control" placeholder="Deck name" />
            <div v-if="nameError" class="text-danger mt-2">
              {{ nameError }}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeNewDeckDialog">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" :disabled="!isModalNameValid" @click="confirmCreateDeck">
              Create
            </button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>

    <!-- Upload Deck -->
    <div class="upload-deck mt-4">
      <h3>Upload Deck</h3>
      <div class="input-group">
        <input type="file" @change="uploadDeck" class="form-control" accept=".json" />
      </div>
    </div>

    <!-- Deck Grid -->
    <div class="deck-grid row mt-4">
      <div v-for="deck in deckBox" :key="deck.name" class="col-xl-2 col-lg-3 col-md-4 col-sm-12 mb-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column">
            <h5 class="card-title text-truncate text-center fs-4">
              {{ deck.name }}
              <i v-if="deckValidations[deck.name]" class="bi bi-exclamation-circle-fill text-danger ms-1"
                :title="deckValidations[deck.name].join('; ')"></i>
            </h5>
            <p class="card-text mb-4 text-center">
              {{ cardCount(deck) }} card{{ cardCount(deck) === 1 ? "" : "s" }}
            </p>
            <div class="mt-auto d-flex justify-content-between">
              <button class="btn btn-success btn-sm me-1" @click.stop="selectThisDeck(deck)" title="Edit Deck">
                <i class="bi bi-arrow-right-circle"></i>
              </button>
              <button class="btn btn-primary btn-sm me-1" @click.stop="renameDeckPrompt(deck)" title="Rename Deck">
                <i class="bi bi-cursor-text"></i>
              </button>
              <button class="btn btn-primary btn-sm me-1" @click.stop="downloadDeck(deck)" title="Download Deck">
                <i class="bi bi-download"></i>
              </button>
              <button class="btn btn-primary btn-sm me-1" @click.stop="copyDeckPrompt(deck)" title="Copy Deck">
                <i class="bi bi-files"></i>
              </button>
              <button class="btn btn-danger btn-sm" @click.stop="deleteDeckPrompt(deck.name)" title="Delete Deck">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!deckBox.length" class="col-12 text-center text-muted">
        No decks yet.
      </div>
    </div>
  </div>
</template>

<script>
import { inject, ref, computed, onMounted, watch } from "vue";
import {
  getDeckBox,
  saveDeckBox,
  createDeck,
  deleteDeck,
  renameDeck,
  copyDeck,
} from "../services/DeckService";

export default {
  name: "DeckBuilderComponent",
  setup() {
    // Deck data
    const deckBox = ref(getDeckBox());
    const save = () => saveDeckBox(deckBox.value);

    // Validation state
    const deckValidations = ref({});
    function validateDeck(deck) {
      const total = deck.cards.reduce((sum, c) => sum + (c.count || 0), 0);
      const msgs = [];
      if (total !== 40) {
        msgs.push("Deck must contain exactly 40 cards");
      }
      const missingAbilities = deck.cards.some(
        (c) => !Array.isArray(c.abilities) || c.abilities.length === 0
      );
      if (missingAbilities) {
        msgs.push("Each card must have at least one ability.");
      }
      return msgs;
    }
    function runValidations() {
      const all = {};
      deckBox.value.forEach((deck) => {
        const m = validateDeck(deck);
        if (m.length) all[deck.name] = m;
      });
      deckValidations.value = all;
    }
    onMounted(runValidations);
    // Re-run validations whenever the deck contents or counts change
    watch(deckBox, runValidations, { deep: true });

    // Helper to get total card count in a deck
    const cardCount = (deck) =>
      deck.cards.reduce((sum, c) => sum + (c.count || 0), 0);

    // Context
    const selectedDeck = inject("selectedDeck");
    const selectDeck = inject("selectDeck");
    const deselectDeck = inject("deselectDeck");

    // New deck dialog
    const showNewDeckDialog = ref(false);
    const modalDeckName = ref("");
    const openNewDeckDialog = () => {
      modalDeckName.value = "";
      showNewDeckDialog.value = true;
    };
    const closeNewDeckDialog = () => {
      showNewDeckDialog.value = false;
    };
    const nameError = computed(() => {
      const nm = modalDeckName.value.trim();
      if (!nm) return "Name cannot be empty.";
      if (deckBox.value.some((d) => d.name === nm)) return "Name already exists.";
      return "";
    });
    const isModalNameValid = computed(() => !nameError.value);
    const confirmCreateDeck = () => {
      const nm = modalDeckName.value.trim();
      createDeck(nm);
      deckBox.value = getDeckBox();
      save();
      selectDeck(nm);
      runValidations();
      showNewDeckDialog.value = false;
    };

    // Upload deck
    const uploadDeck = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const r = new FileReader();
      r.onload = (ev) => {
        try {
          const d = JSON.parse(ev.target.result);
          if (!d.name || !Array.isArray(d.cards)) {
            alert("Invalid deck format.");
            return;
          }
          if (deckBox.value.some((x) => x.name === d.name)) {
            alert("A deck with this name already exists.");
            return;
          }
          deckBox.value.push(d);
          save();
          runValidations();
        } catch {
          alert("Failed to upload deck.");
        }
      };
      r.readAsText(file);
    };

    // Download deck
    const downloadDeck = (deck) => {
      const data = JSON.stringify(deck, null, 2);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${deck.name}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    // Deck operations
    const selectThisDeck = (deck) => selectDeck(deck.name);

    const renameDeckPrompt = (deck) => {
      const nm = prompt("New name for deck:", deck.name)?.trim();
      if (!nm || nm === deck.name) return;
      if (deckBox.value.some((d) => d.name === nm && d.name !== deck.name)) {
        alert("A deck with this name already exists.");
        return;
      }
      renameDeck(deck.name, nm);
      deckBox.value = getDeckBox();
      save();
      runValidations();
    };

    const copyDeckPrompt = (deck) => {
      const nm = prompt("Name for the copy:", deck.name + " Copy")?.trim();
      if (!nm) return;
      if (deckBox.value.some((d) => d.name === nm)) {
        alert("A deck with this name already exists.");
        return;
      }
      copyDeck(deck.name, nm);
      deckBox.value = getDeckBox();
      save();
      runValidations();
      selectDeck(nm);
    };

    const deleteDeckPrompt = (name) => {
      if (!confirm(`Delete deck "${name}"?`)) return;
      deleteDeck(name);
      deckBox.value = getDeckBox();
      save();
      runValidations();
      if (selectedDeck.value?.name === name) deselectDeck();
    };

    // For future card-list styling
    const elementColors = {
      Neutral: "#cccccc",
      Abyssal: "#45818e",
      Arcane: "#bd94a8",
      Force: "#dd7e6b",
      Light: "#f1c232",
      Noxious: "#8e7cc3",
      Storm: "#b6d7a8",
      Terra: "#f9cb9c",
      Thermal: "#6d9eeb",
    };
    const getElementColumnStyle = (card) => {
      const elements = card.element || [];
      if (elements.length === 1) {
        return { backgroundColor: elementColors[elements[0]] };
      } else if (elements.length > 1) {
        const grad = elements
          .map((el) => elementColors[el] || "#ffffff")
          .join(", ");
        return { backgroundImage: `linear-gradient(to right, ${grad})` };
      }
      return {};
    };

    return {
      deckBox,
      selectedDeck,
      deckValidations,
      openNewDeckDialog,
      showNewDeckDialog,
      modalDeckName,
      nameError,
      isModalNameValid,
      confirmCreateDeck,
      closeNewDeckDialog,
      uploadDeck,
      downloadDeck,
      selectThisDeck,
      renameDeckPrompt,
      copyDeckPrompt,
      deleteDeckPrompt,
      getElementColumnStyle,
      cardCount,
    };
  },
};
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
